import React, { useState, useRef, useEffect } from 'react'
import MuiLink from '@material-ui/core/Link'
import PropTypes from 'prop-types'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import Chip from '@material-ui/core/Chip'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import {
  Calendar,
  MapPin,
  Users,
  COACH_PROFILE_DIALOG_META_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import CloseIcon from '@material-ui/icons/Close'
import FavoriteIcon from '@material-ui/icons/Favorite'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import ButtonCta from '@components/atoms/button-cta'
import defaults, { metersPerMile } from '@config/defaults'
import { makeStyles } from '@material-ui/core/styles'
import useIsMobile from '@hooks/useIsMobile'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import {
  formatRegistrationOpensCaption,
  getGroupEventOfferingListCtaLabel,
  shouldShowRegistrationOpensLine,
} from '@lib/pga-coach/group-event-registration'
import MarkdownText from './markdown-text'

const useStyles = makeStyles((theme) => ({
  modalPaper: {
    borderRadius: 12,
    maxWidth: 680,
    '&.MuiDialog-paperFullScreen': {
      borderRadius: 0,
    },
  },
  modalContent: {
    padding: 0,
    paddingTop: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalScrollArea: {
    flex: 1,
    overflowY: 'auto',
  },
  modalHeaderImageWrap: {
    position: 'relative',
    width: '100%',
    height: 300,
    backgroundColor: '#e7eef6',
    [theme.breakpoints.down('xs')]: {
      height: 220,
    },
  },
  modalHeaderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  modalHeaderActions: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  modalHeaderActionButton: {
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(15, 34, 52, 0.18)',
    padding: theme.spacing(0.75),
    '&:hover': {
      backgroundColor: '#f5f7fa',
    },
  },
  modalFavoriteIconSaved: {
    color: theme.palette.blue.main,
  },
  modalBody: {
    padding: theme.spacing(2.5, 3, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  /** Tag chips at top of body (replaces standalone format chip). */
  modalChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.25),
  },
  modalTitle: {
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: theme.spacing(1.25),
  },
  modalDescription: {
    color: '#5f6771',
    fontSize: '1.06rem',
    lineHeight: 1.45,
    whiteSpace: 'pre-line',
    marginBottom: theme.spacing(0.5),
    '& .MuiTypography-root': {
      color: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
    },
  },
  modalDescriptionClamped: {
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 4,
    '-webkit-box-orient': 'vertical',
  },
  modalDescriptionWrap: {
    marginBottom: theme.spacing(2),
  },
  modalCoachRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    backgroundColor: '#f0f2f5',
    marginBottom: theme.spacing(2),
  },
  modalCoachAvatar: {
    width: 56,
    height: 56,
    flexShrink: 0,
  },
  modalCoachInfo: {
    flex: 1,
    minWidth: 0,
  },
  modalCoachName: {
    fontWeight: 700,
    marginBottom: 2,
  },
  modalCoachNameLink: {
    fontWeight: 700,
    marginBottom: 2,
    display: 'block',
    textDecoration: 'none',
    color: theme.palette.blue.main,
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.blue.main,
    },
  },
  modalCoachText: {
    color: '#596270',
    lineHeight: 1.35,
    whiteSpace: 'pre-line',
    '& .MuiTypography-root': {
      color: 'inherit',
      lineHeight: 1.35,
    },
  },
  modalCoachTextClamped: {
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  modalCoachToggle: {
    background: 'none',
    border: 'none',
    padding: theme.spacing(0.5, 0.75),
    marginTop: theme.spacing(0.25),
    marginLeft: theme.spacing(-0.75),
    cursor: 'pointer',
    color: theme.palette.blue.main,
    fontSize: '0.8rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    lineHeight: 1.35,
    display: 'inline-block',
    verticalAlign: 'baseline',
    textAlign: 'left',
  },
  modalMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  modalMetaIcon: {
    flexShrink: 0,
    fontSize: '1.375rem',
    color: theme.palette.text.secondary,
  },
  modalMetaTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  modalMetaTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.25,
    color: '#2a3240',
  },
  modalMetaSub: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.35,
    color: '#687381',
    marginTop: theme.spacing(0.25),
  },
  modalChip: {
    '&.MuiChip-root': {
      borderRadius: 999,
    },
    height: 'auto',
    minHeight: 28,
    border: '1px solid #dfe3ea',
    backgroundColor: theme.palette.common.white,
    color: '#4f5966',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(1.25),
      paddingTop: theme.spacing(0.375),
      paddingBottom: theme.spacing(0.375),
      lineHeight: 1.2,
    },
  },
  modalPriceProminent: {
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: 0,
  },
  modalFooter: {
    borderTop: '1px solid #dfe3ea',
    padding: theme.spacing(1.75, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexShrink: 0,
    backgroundColor: '#fff',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  modalBookButton: {
    '&.MuiButton-root': {
      borderRadius: 999,
    },
    minHeight: 32,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: 1.2,
    padding: theme.spacing(0.75, 2.25),
    whiteSpace: 'nowrap',
    boxShadow: 'none',
  },
}))

const formatDuration = (durationInMinutes) => {
  if (!durationInMinutes || Number.isNaN(Number(durationInMinutes))) return null
  const value = Number(durationInMinutes)
  if (value < 60) return `${value} min`
  if (value % 60 === 0) {
    const hours = value / 60
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
  const hours = Math.floor(value / 60)
  const minutes = value % 60
  return `${hours}h ${minutes}m`
}

const formatDistance = (geoDistanceMeters) => {
  if (geoDistanceMeters == null) return null
  const miles = Number(geoDistanceMeters) / metersPerMile
  if (Number.isNaN(miles)) return null
  return `${miles.toFixed(1)} mi`
}

/** Middle-dot separated secondary detail line (journey modal mock). */
const joinOfferingMetaDetails = (parts) => parts.filter(Boolean).join(' · ')

const toTitleFormat = (value = '') => value
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ')

function GroupProgramOfferingDialog({
  open,
  onClose,
  offering,
  formatPrice,
  formatLocation,
  formatJrlDateRange,
  getTagLabels,
  onPrimaryActionClick,
  isOfferingFavorite,
  onToggleOfferingFavorite,
}) {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const [bioExpanded, setBioExpanded] = useState(false)
  const [bioOverflows, setBioOverflows] = useState(false)
  const bioRef = useRef(null)
  const [descExpanded, setDescExpanded] = useState(false)
  const [descOverflows, setDescOverflows] = useState(false)
  const descRef = useRef(null)

  useEffect(() => {
    if (!open) {
      setBioExpanded(false)
      setBioOverflows(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => {
      if (bioRef.current) {
        setBioOverflows(bioRef.current.scrollHeight > bioRef.current.clientHeight)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [open, offering.coach.bio])

  useEffect(() => {
    if (!open) {
      setDescExpanded(false)
      setDescOverflows(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => {
      if (descRef.current) {
        setDescOverflows(descRef.current.scrollHeight > descRef.current.clientHeight)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [open, offering.description])

  const {
    maxAttendance,
    registrationCount = 0,
    geoDistanceMeters,
    nextAvailableAt,
    durationInMinutes,
    ctaUrl,
    venueName,
    format,
    coachFacilityFullAddress,
  } = offering
  const isWaitlistCta = Boolean(offering.registrationFull && offering.hasWaitlist)
  const registrationOpensCaption = shouldShowRegistrationOpensLine(offering)
    ? formatRegistrationOpensCaption(offering.registrationOpenDateTime, offering.timezone)
    : null
  const spotsRemaining = maxAttendance != null
    ? Math.max(maxAttendance - registrationCount, 0)
    : null
  const distanceLabel = formatDistance(geoDistanceMeters)
  const tz = offering.timezone || 'UTC'
  const nextAvailableAtDate = nextAvailableAt ? new Date(nextAvailableAt) : null
  const availabilityDate = nextAvailableAtDate && !Number.isNaN(nextAvailableAtDate.getTime())
    ? nextAvailableAtDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: tz,
    })
    : null
  const availabilityTime = nextAvailableAtDate && !Number.isNaN(nextAvailableAtDate.getTime())
    ? nextAvailableAtDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
    })
    : null
  const durationLabel = formatDuration(durationInMinutes)
  const scheduleSecondary = joinOfferingMetaDetails([availabilityTime, durationLabel])
  const locationSecondary = joinOfferingMetaDetails([distanceLabel, coachFacilityFullAddress])
  const modalCtaLabel = isWaitlistCta ? 'Join Waitlist' : getGroupEventOfferingListCtaLabel(offering)
  const tagLabels = getTagLabels(offering)
  let capacityTitle = 'No registration limit'
  let capacitySub = null
  if (isWaitlistCta) {
    capacityTitle = 'Waitlist open'
    if (maxAttendance > 0) capacitySub = `Max ${maxAttendance} golfers`
  } else if (maxAttendance != null) {
    capacityTitle = `${spotsRemaining} spots remaining`
    capacitySub = `Max ${maxAttendance} golfers`
  }

  const handleDialogClose = (event) => {
    event?.stopPropagation?.()
    onClose()
  }

  const handleBookNowClick = (event) => {
    event.stopPropagation()
    onPrimaryActionClick?.({
      dialog_name: 'group_program_offering_dialog',
      button_name: modalCtaLabel,
    })
    onClose()
  }

  const imageUrl = offering.imageUrl || offering.coach.photoUrl || defaults.coachPlaceholder
  const imageUrlOptimized = imageUrl ? composeOptimizedImageUrl(imageUrl, 1200) : null

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.modalPaper }}
    >
      <DialogContent className={classes.modalContent}>
        <div className={classes.modalScrollArea}>
          <div className={classes.modalHeaderImageWrap}>
            <img
              src={imageUrlOptimized}
              alt={offering.title || 'Event image'}
              className={classes.modalHeaderImage}
            />
            <div className={classes.modalHeaderActions}>
              {typeof onToggleOfferingFavorite === 'function' && (
                <Tooltip title={isOfferingFavorite ? 'Remove from saved offerings' : 'Save offering'}>
                  <IconButton
                    size="small"
                    aria-label={isOfferingFavorite ? 'Remove offering from saved list' : 'Save offering to your list'}
                    aria-pressed={Boolean(isOfferingFavorite)}
                    className={classes.modalHeaderActionButton}
                    onClick={(event) => {
                      event.stopPropagation()
                      onToggleOfferingFavorite()
                    }}
                  >
                    {isOfferingFavorite
                      ? <FavoriteIcon className={classes.modalFavoriteIconSaved} fontSize="small" />
                      : <FavoriteBorderIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                className={classes.modalHeaderActionButton}
                aria-label="Close event details"
                onClick={handleDialogClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>

          <div className={classes.modalBody}>
            {tagLabels.length > 0 && (
              <Box className={classes.modalChips}>
                {tagLabels.map((label) => (
                  <Chip key={label} label={label} size="small" className={classes.modalChip} />
                ))}
              </Box>
            )}
            <Typography variant="h4" className={classes.modalTitle}>
              {offering.title}
            </Typography>

            {offering.description && (
              <div className={classes.modalDescriptionWrap}>
                <div
                  ref={descRef}
                  className={`${classes.modalDescription} ${!descExpanded ? classes.modalDescriptionClamped : ''}`}
                >
                  <MarkdownText paragraphVariant="body1">
                    {offering.description}
                  </MarkdownText>
                </div>
                {descOverflows && (
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    className={classes.modalCoachToggle}
                    onClick={() => setDescExpanded((prev) => !prev)}
                  >
                    {descExpanded ? 'Show less' : 'More info'}
                  </MuiLink>
                )}
              </div>
            )}

            <div className={classes.modalCoachRow}>
              <Avatar
                className={classes.modalCoachAvatar}
                src={offering.coach.photoUrl || defaults.coachPlaceholder}
                alt={offering.coach.name || 'Coach'}
              />
              <div className={classes.modalCoachInfo}>
                {offering.coach.slug ? (
                  <MuiLink
                    href={`/coach/${offering.coach.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    className={classes.modalCoachNameLink}
                  >
                    {offering.coach.name || 'PGA Coach'}
                  </MuiLink>
                ) : (
                  <Typography className={classes.modalCoachName}>
                    {offering.coach.name || 'PGA Coach'}
                  </Typography>
                )}
                {(offering.coach.bio || offering.coach.credentials) && (
                  <>
                    <div
                      ref={bioRef}
                      className={`${classes.modalCoachText} ${!bioExpanded ? classes.modalCoachTextClamped : ''}`}
                    >
                      {offering.coach.bio ? (
                        <MarkdownText paragraphVariant="body1">
                          {offering.coach.bio}
                        </MarkdownText>
                      ) : (
                        toTitleFormat(offering.coach.credentials)
                      )}
                    </div>
                    {bioOverflows && (
                      <MuiLink
                        component="button"
                        type="button"
                        underline="hover"
                        className={classes.modalCoachToggle}
                        onClick={() => setBioExpanded((prev) => !prev)}
                      >
                        {bioExpanded ? 'Show less' : 'More info'}
                      </MuiLink>
                    )}
                  </>
                )}
              </div>
            </div>

            {(venueName || coachFacilityFullAddress || distanceLabel) && (
              <div className={classes.modalMetaRow}>
                <MapPin {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                <div className={classes.modalMetaTextBlock}>
                  <Typography component="p" className={classes.modalMetaTitle}>
                    {formatLocation(offering)}
                  </Typography>
                  {locationSecondary ? (
                    <Typography component="p" className={classes.modalMetaSub}>
                      {locationSecondary}
                    </Typography>
                  ) : null}
                </div>
              </div>
            )}

            {format === 'program' ? (
              <div className={classes.modalMetaRow}>
                <Calendar {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                <div className={classes.modalMetaTextBlock}>
                  <Typography component="p" className={classes.modalMetaTitle}>
                    {formatJrlDateRange(offering)}
                  </Typography>
                </div>
              </div>
            ) : (availabilityDate || scheduleSecondary) && (
              <div className={classes.modalMetaRow}>
                <Calendar {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                <div className={classes.modalMetaTextBlock}>
                  {availabilityDate ? (
                    <Typography component="p" className={classes.modalMetaTitle}>
                      {availabilityDate}
                    </Typography>
                  ) : null}
                  {scheduleSecondary ? (
                    <Typography
                      component="p"
                      className={availabilityDate ? classes.modalMetaSub : classes.modalMetaTitle}
                    >
                      {scheduleSecondary}
                    </Typography>
                  ) : null}
                </div>
              </div>
            )}

            {registrationOpensCaption ? (
              <div className={classes.modalMetaRow}>
                <Calendar {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                <div className={classes.modalMetaTextBlock}>
                  <Typography component="p" className={classes.modalMetaTitle}>
                    {registrationOpensCaption}
                  </Typography>
                </div>
              </div>
            ) : null}

            <div className={classes.modalMetaRow}>
              <Users {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
              <div className={classes.modalMetaTextBlock}>
                <Typography component="p" className={classes.modalMetaTitle}>
                  {capacityTitle}
                </Typography>
                {capacitySub ? (
                  <Typography component="p" className={classes.modalMetaSub}>
                    {capacitySub}
                  </Typography>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className={classes.modalFooter}>
          <Typography variant="h4" component="p" className={classes.modalPriceProminent}>
            {formatPrice(offering)}
          </Typography>

          <div>
            {ctaUrl && (
              <ButtonCta
                externalLink={ctaUrl}
                color="primary"
                variant="contained"
                className={classes.modalBookButton}
                onClick={handleBookNowClick}
              >
                {modalCtaLabel}
              </ButtonCta>
            )}
            {!ctaUrl && (
              <Button
                color="primary"
                variant="contained"
                className={classes.modalBookButton}
                onClick={handleDialogClose}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

GroupProgramOfferingDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  offering: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    format: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaType: PropTypes.string,
    imageUrl: PropTypes.string,
    venueName: PropTypes.string,
    nextAvailableAt: PropTypes.instanceOf(Date),
    startDateTime: PropTypes.instanceOf(Date),
    endDateTime: PropTypes.instanceOf(Date),
    timezone: PropTypes.string,
    coachFacilityFullAddress: PropTypes.string,
    maxAttendance: PropTypes.number,
    registrationCount: PropTypes.number,
    registrationFull: PropTypes.bool,
    hasWaitlist: PropTypes.bool,
    geoDistanceMeters: PropTypes.number,
    durationInMinutes: PropTypes.number,
    coach: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      credentials: PropTypes.string,
      bio: PropTypes.string,
      photoUrl: PropTypes.string,
    }),
  }).isRequired,
  formatPrice: PropTypes.func.isRequired,
  formatLocation: PropTypes.func.isRequired,
  formatJrlDateRange: PropTypes.func.isRequired,
  getTagLabels: PropTypes.func.isRequired,
  onPrimaryActionClick: PropTypes.func,
  isOfferingFavorite: PropTypes.bool,
  onToggleOfferingFavorite: PropTypes.func,
}

GroupProgramOfferingDialog.defaultProps = {
  open: false,
  onPrimaryActionClick: undefined,
  isOfferingFavorite: false,
  onToggleOfferingFavorite: undefined,
}

export default GroupProgramOfferingDialog
